import MenuOutlinedIcon from '@uala/icons/MenuOutlinedIcon';
import React, { createContext, useCallback, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '../Button';
import useDesktopSize from '../hooks/useDesktopSize';
import UalaLogo from '../images/logotipo.svg';
import {
  MenuProfileInfo,
  StyledHeader,
  StyledHeaderAction,
  StyledLogoTipo,
  StyledMenuButton
} from './styles';

interface HeaderProps {
  onMenuOpen: () => void;
  onSignOut: () => void;
  userPicture?: string;
  userName: string;
}

type HeaderActionPortal = (
  node?: React.ReactNode
) => React.ReactNode | React.ReactPortal | null;
interface HeaderActionsPortal {
  headerRightActionPortal: HeaderActionPortal;
  headerLeftActionPortal: HeaderActionPortal;
}

const HeaderContext = createContext<HeaderActionsPortal>({
  headerRightActionPortal: () => null,
  headerLeftActionPortal: () => null
});

const Header: React.FC<HeaderProps> = ({
  onMenuOpen,
  onSignOut,
  userPicture,
  userName,
  children
}) => {
  const isDesktop = useDesktopSize();
  const headerLeftAction = useRef<HTMLDivElement>(null);
  const headerRightAction = useRef<HTMLDivElement>(null);

  const headerLeftActionPortal = useCallback((node: React.ReactNode) => {
    if (!headerLeftAction.current) return node;
    return createPortal(node, headerLeftAction.current);
  }, []);

  const headerRightActionPortal = useCallback((node: React.ReactNode) => {
    if (!headerRightAction.current) return node;
    return createPortal(node, headerRightAction.current);
  }, []);

  return (
    <>
      <StyledHeader id="web-header-container">
        <StyledHeaderAction direction="left" ref={headerLeftAction}>
          <StyledMenuButton data-testid="hamburger-button" onClick={onMenuOpen}>
            <MenuOutlinedIcon />
          </StyledMenuButton>
        </StyledHeaderAction>
        <StyledLogoTipo width="50" height="25" src={UalaLogo} alt="Ualá" />
        {isDesktop && (
          <>
            <MenuProfileInfo
              userPicture={userPicture}
              userName={userName}
              size="medium"
            />
            <Button
              variant="unfilled"
              marginLeft="auto"
              id="web-header-logout"
              onClick={onSignOut}
            >
              Cerrar sesión
            </Button>
          </>
        )}
        <StyledHeaderAction
          direction="right"
          ref={headerRightAction}
        ></StyledHeaderAction>
      </StyledHeader>
      <HeaderContext.Provider
        value={{ headerRightActionPortal, headerLeftActionPortal }}
      >
        {children}
      </HeaderContext.Provider>
    </>
  );
};

export const useHeaderAction = (): HeaderActionsPortal => {
  const { headerRightActionPortal, headerLeftActionPortal } =
    useContext(HeaderContext);
  return {
    headerRightActionPortal,
    headerLeftActionPortal
  };
};

export default Header;
